import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { ScenarioPhase } from '@/versions/v1/schema/scenario';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { RecirculatingLoop, Stream, StreamSpec } from '@/versions/v1/schema/stream';
import type {
  InsightKind,
  NodeInsight,
  ProcessConstraint,
  SamplePoint,
  Subprocess,
  UnitHealth,
  UnitOperation,
  UtilizationRank,
} from '@/versions/v1/schema/unit-operation';
import type { Reading, ReadingTone } from '@/versions/v1/schema/reading';
import { nodeDisplays } from './NodeDisplays.transform';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import {
  isInteriorStream,
  subprocessForNodeId,
} from '@/versions/v1/shared/bunker-hill-plant-nodes';
import {
  NODE_INSIGHTS,
  PROCESS_CONSTRAINTS,
  RECIRCULATING_LOOPS,
  SAMPLE_POINTS,
  STREAM_SPECS,
  STREAMS,
  SUBPROCESSES,
  UNIT_OPERATIONS,
  UTILIZATION_RANKS,
} from '@/versions/v1/shared/bunker-hill-seed';
import {
  FMEA_VINTAGE,
  RULE_CLAIMS,
  isIngestedRuleClaim,
} from '@/versions/v1/shared/bunker-hill-rule-claims';
import { ruleById, rulesForNode } from '@/versions/v1/shared/rule-scope';
import { CONSTRAINT_DISPLAY_OVERRIDE, READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { isHardOreZone, oreZoneAt } from '@/versions/v1/shared/bunker-hill-ore-zones';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { readingsAt, seriesAt, valueAt } from '@/versions/v1/shared/series.derive';
import { plantPhrase } from '@/versions/v1/shared/project-seed';
import { controlChart, type ControlChartViewModel } from '@/versions/v1/shared/SpcEngine';
import { diffMinutes, shiftOf } from '@/versions/v1/shared/clock.derive';
import {
  formatUnitKeyParam,
  formatValue,
  healthForReadingIds,
  readingStatus,
  scopeHealth,
  utilisationPct,
} from '@/versions/v1/shared/reading.derive';
import type { VizTone } from '@/versions/v1/modules/_viz';

export type IngestionCopy = {
  title: string;
  description: string;
};

export const LATENCY_LABEL: Record<SamplePoint['latencyClass'], string> = {
  online: 'On-line',
  rush: 'Rush lab < 1 h',
  shift: 'Shift assay < 3 h',
  composite: '24 h composite',
};

export function ingestionCopy(plantName: string): IngestionCopy {
  return {
    title: 'Plant model',
    description: `Bottleneck and spec-conformance for ${plantPhrase(plantName)} — crusher to paste, ranked by utilization.`,
  };
}

export function bunkerHillUnits(): readonly UnitOperation[] {
  return UNIT_OPERATIONS;
}

export function bunkerHillSubprocesses(): readonly Subprocess[] {
  return SUBPROCESSES.slice().sort((a, b) => a.sequenceOrder - b.sequenceOrder);
}

export function bunkerHillConstraints(): readonly ProcessConstraint[] {
  return PROCESS_CONSTRAINTS;
}

/** Derive: SA01 pending while hardness discriminator open; LATE when satisfied. */
export function applySamplePhase(
  points: readonly SamplePoint[],
  diagnosis: Diagnosis,
  state: SessionState,
): SamplePoint[] {
  const hardness = diagnosis.candidates.find((row) => row.rule.id === 'dr-hardness');
  if (!hardness) return points.map((point) => ({ ...point }));
  const discReadingId = hardness.rule.discriminator
    ? TAG_MAPPING.find((row) => row.role === hardness.rule.discriminator!.signal)?.readingId
    : undefined;
  const p80 =
    hardness.discriminatorSatisfied && discReadingId ? valueAt(discReadingId, state) : undefined;
  return points.map((point) => {
    if (point.id !== 'SA01') return { ...point };
    if (!hardness.discriminatorOpen && hardness.discriminatorSatisfied && p80 !== undefined) {
      return { ...point, adherence: 'LATE', spec: `P80 ${p80} µm vs 74 µm design` };
    }
    if (hardness.discriminatorOpen || hardness.confidencePct >= 40) {
      return { ...point, adherence: 'PENDING', spec: 'P80 = 74 µm · assay not back' };
    }
    return { ...point };
  });
}

function constraintReadingFor(row: ProcessConstraint, readings: readonly Reading[]): Reading | undefined {
  return readings.find((reading) => reading.id === `rd-${row.id}`);
}

function resolveRankPct(
  rank: UtilizationRank,
  readings: readonly Reading[],
  phase: ScenarioPhase,
): number {
  if (rank.id === 'u-grind' && phase !== 'idle') {
    return phase === 'confirmed' ? 94 : 91;
  }
  // K10b round 3 (#518): at the default (idle) phase grinding's share of band is
  // the measured daily feed over the 90 stph ceiling — `rd-unit-bm01-feed` is
  // `rd-kpi-ore-td ÷ 24` via `series.derive.ts`, so this reads 80%
  // (`71.666667 / 90`), the default bottleneck percentage K10 row 132 set.
  // `rd-c-feed` is the hourly `rs-wit-021` alias and still resolves 75 / 90 =
  // 83%; its re-base is deferred past the demo, so the default read must not use
  // it. The provisional / confirmed scenario reads keep their values above.
  if (rank.id === 'u-grind') {
    const dailyFeed = readings.find((row) => row.id === 'rd-unit-bm01-feed');
    const pct = dailyFeed ? utilisationPct(dailyFeed) : undefined;
    if (pct !== undefined) return pct;
  }
  if (!rank.computed && rank.utilizationPct !== undefined) {
    return rank.utilizationPct;
  }
  if (!rank.readingId) return rank.utilizationPct ?? 0;
  const reading = readings.find((row) => row.id === rank.readingId);
  if (!reading) return rank.utilizationPct ?? 0;
  return utilisationPct(reading) ?? rank.utilizationPct ?? 0;
}

export type ResolvedUtilizationRank = UtilizationRank & { utilizationPct: number };

/** Derive utilization ranks from series-projected readings. */
export function applyUtilization(
  ranks: readonly UtilizationRank[],
  phase: ScenarioPhase,
  state: SessionState,
): ResolvedUtilizationRank[] {
  const readings = readingsAt(state);
  return ranks
    .map((row) => ({
      ...row,
      utilizationPct: resolveRankPct(row, readings, phase),
    }))
    .slice()
    .sort((a, b) => b.utilizationPct - a.utilizationPct);
}

export function readingToneToHealth(tone: ReadingTone): UnitHealth {
  if (tone === 'bad') return 'alarm';
  if (tone === 'warn') return 'warn';
  return 'ok';
}

export function bunkerHillSamplePoints(): readonly SamplePoint[] {
  return SAMPLE_POINTS;
}

export function bunkerHillUtilization(): readonly UtilizationRank[] {
  return UTILIZATION_RANKS;
}

/** Derive: constraint utilisation minus runner-up from ranked headroom table. */
export function headroomToRunnerUp(ranks: readonly ResolvedUtilizationRank[]): number {
  const subprocess = ranks.filter((row) => row.scope === 'subprocess');
  const sorted = subprocess.slice().sort((a, b) => b.utilizationPct - a.utilizationPct);
  const constraint = sorted.find((row) => row.isBottleneck) ?? sorted[0];
  const runnerUp = sorted.find((row) => row.id !== constraint?.id) ?? sorted[1];
  if (!constraint || !runnerUp) return 0;
  return constraint.utilizationPct - runnerUp.utilizationPct;
}

function grindingZoneClause(cursor: SessionState['cursor']): string {
  const zone = oreZoneAt(cursor);
  if (!zone || !isHardOreZone(zone)) return '';
  const agLabel = zone.character.agHead === 'high' ? 'high Ag' : `${zone.character.agHead} Ag`;
  return `, while feed comes from the ${zone.label} (${zone.character.hardness}, ${agLabel})`;
}

/** Splice a zone clause before the sentence's terminal full stop. */
function spliceBeforeStop(sentence: string, clause: string): string {
  if (!clause) return sentence;
  const lastPeriod = sentence.lastIndexOf('.');
  if (lastPeriod === -1) return `${sentence}${clause}`;
  return `${sentence.slice(0, lastPeriod)}${clause}${sentence.slice(lastPeriod)}`;
}

/** `omitFeed` drops the measured feed from the design-basis sentence where the
 *  page already states it (the Plant model's Crushing cell owns 71.7 stph —
 *  polish.P11 r1 "say it once"); the utilisation against the band stays. */
export function bottleneckRead(
  state: SessionState,
  advisory?: AdvisorySessionState,
  options: { omitFeed?: boolean } = {},
): string {
  const diagnosis = diagnose(state, advisory);
  const hardnessCase = diagnosis.cases.find((row) => row.kind === 'hardness');
  const hardness = diagnosis.candidates.find((row) => row.rule.id === 'dr-hardness');
  const zoneClause = grindingZoneClause(state.cursor);
  if (!hardnessCase || !hardness) {
    const readings = readingsAt(state);
    const ore = readings.find((row) => row.id === 'rd-kpi-ore-td')!;
    const feedStph = ore.value! / 24;
    const bandHigh = readings.find((row) => row.id === 'rd-c-feed')!.band!.hi!;
    const cycloneCount = readings.find((row) => row.id === 'rd-c-cyc-n')!;
    const recycle = RECIRCULATING_LOOPS.find((row) => row.id === 'loop-cyc')!.loadRatio;
    return spliceBeforeStop(
      `Grinding (mill + cyclone) — ${options.omitFeed ? '' : `${feedStph.toFixed(1)} stph, `}${Math.round(feedStph / bandHigh * 100)}% of the ${bandHigh} stph normal-band ceiling; ${cycloneCount.value} of ${cycloneCount.dcsRange!.hi} cyclones in service; recycle ${recycle}.`,
      zoneClause,
    );
  }
  const mediaScore =
    diagnosis.candidates.find((row) => row.rule.id === 'dr-media')?.confidencePct ?? 0;
  const rivalLine = ` (${hardness.confidencePct}% hardness vs media ${mediaScore}%)`;
  if (hardnessCase.state === 'routed') {
    return `${spliceBeforeStop(
      `Confirmed: ${hardnessCase.observation} Grinding is the live constraint until ore reverts or feed is cut 10%. Circulating load remains the leading indicator.`,
      zoneClause,
    )}${rivalLine}`;
  }
  return `${spliceBeforeStop(`Provisional: ${hardnessCase.observation}`, zoneClause)}${rivalLine} SA01 pending — do not wait 3 h to say something.`;
}

export type PlantStripNode = {
  id: string;
  shortName: string;
  headline: string | null;
  sub: string | null;
  utilizationPct: number | null;
  isBottleneck: boolean;
  /** Internal-loop loadRatio, e.g. grind's "248% of new feed". */
  recycleBadge: string | null;
  /** Process-water utility cell — dashed border, "Utility loop" label. */
  utility?: boolean;
};

/** Display labels — topology comes from STREAMS, not this map. */
const STRIP_SHORT_NAME: Record<string, string> = {
  'sp-crush': 'Crush',
  'sp-grind': 'Grind',
  'sp-pb': 'Pb / Ag',
  'sp-zn': 'Zn float',
  'sp-filter': 'Thicken',
  'sp-paste': 'Paste',
  'sp-water': 'Water',
};

const STRIP_EXCLUDED = new Set(['external']);
const INTERIOR_SAMPLE_NODE = 'sp-pb';

function rankForNode(
  nodeId: string,
  ranks: readonly UtilizationRank[],
): UtilizationRank | undefined {
  return ranks.find((row) => row.id === `u-${nodeId.slice(3)}`);
}

function nodeStreamIds(
  nodeId: string,
  streams: readonly Stream[],
): Set<string> {
  return new Set(
    streams.filter((edge) => edge.from === nodeId || edge.to === nodeId).map((edge) => edge.id),
  );
}

function recycleBadgeForNode(
  nodeId: string,
  streams: readonly Stream[],
  loops: readonly RecirculatingLoop[],
): string | null {
  const ids = nodeStreamIds(nodeId, streams);
  const loop = loops.find(
    (row) => ids.has(row.streamId) && row.scope === 'internal' && row.loadRatio !== '',
  );
  return loop?.loadRatio ?? null;
}

/**
 * Crusher→paste order by walking non-recycle subprocess edges; `sp-water`
 * appends last as the utility loop. sequenceOrder breaks Kahn ties so Zn
 * lands before thicken, matching the material path.
 */
export function stripNodeOrder(
  streams: readonly Stream[] = STREAMS,
  subprocesses: readonly Subprocess[] = SUBPROCESSES,
): string[] {
  const sequence = new Map(subprocesses.map((step) => [step.id, step.sequenceOrder]));
  const nodes = new Set<string>();
  for (const edge of streams) {
    if (edge.isRecycle) continue;
    if (!STRIP_EXCLUDED.has(edge.from) && edge.from.startsWith('sp-')) nodes.add(edge.from);
    if (!STRIP_EXCLUDED.has(edge.to) && edge.to.startsWith('sp-')) nodes.add(edge.to);
  }

  const indegree = new Map<string, number>();
  const adj = new Map<string, string[]>();
  for (const id of nodes) {
    indegree.set(id, 0);
    adj.set(id, []);
  }
  for (const edge of streams) {
    if (edge.isRecycle || edge.from === edge.to) continue;
    if (!nodes.has(edge.from) || !nodes.has(edge.to)) continue;
    adj.get(edge.from)!.push(edge.to);
    indegree.set(edge.to, (indegree.get(edge.to) ?? 0) + 1);
  }

  const bySequence = (a: string, b: string) =>
    (sequence.get(a) ?? 99) - (sequence.get(b) ?? 99);
  const ready = [...nodes].filter((id) => (indegree.get(id) ?? 0) === 0).sort(bySequence);
  const order: string[] = [];
  while (ready.length > 0) {
    const id = ready.shift()!;
    order.push(id);
    for (const next of adj.get(id) ?? []) {
      const remaining = (indegree.get(next) ?? 1) - 1;
      indegree.set(next, remaining);
      if (remaining === 0) {
        ready.push(next);
        ready.sort(bySequence);
      }
    }
  }
  const leftover = [...nodes].filter((id) => !order.includes(id)).sort(bySequence);
  const merged = [...order, ...leftover.filter((id) => !order.includes(id))];
  const withoutWater = merged.filter((id) => id !== 'sp-water');
  if (nodes.has('sp-water')) {
    return [...withoutWater, 'sp-water'];
  }
  return withoutWater;
}

const NODE_AREAS: Record<string, readonly string[]> = {
  'sp-crush': ['Crushing'],
  'sp-grind': ['Grinding', 'Classification'],
  'sp-pb': ['Pb circuit'],
  'sp-zn': ['Zn circuit'],
  'sp-filter': ['Dewatering'],
  'sp-paste': ['Tailings'],
  'sp-water': ['Process water'],
};

function primaryUnitForNode(
  nodeId: string,
  units: readonly UnitOperation[],
): UnitOperation | undefined {
  const areas = new Set(NODE_AREAS[nodeId] ?? []);
  return units.find((unit) => areas.has(unit.area));
}

function splitKeyLabel(label: string): { headline: string; sub: string | null } {
  const split = label.split(' · ');
  if (split.length < 2) {
    return { headline: label, sub: null };
  }
  return { headline: split[0], sub: split.slice(1).join(' · ') };
}

function waterRecycleBadge(streams: readonly Stream[]): string {
  const edge = streams.find((row) => row.id === 'st-water-mill');
  const flow = edge?.nominalFlow.replace(/\s*design$/i, '').trim() ?? '~300 gpm';
  return `Mill water addition · ${flow}`;
}

const INSIGHT_KIND_ORDER: readonly InsightKind[] = ['good', 'bad', 'insight'];

export const INSIGHT_KIND_LABEL: Record<InsightKind, string> = {
  good: 'Good looks like',
  bad: 'Bad looks like',
  insight: 'Human insights / operator notes',
};

const NODE_HUMAN_NAME: Record<string, string> = {
  'sp-crush': 'Ore receiving & crushing',
  'sp-grind': 'Grinding circuit',
  'sp-pb': 'Pb conditioning & flotation',
  'sp-zn': 'Zn conditioning & flotation',
  'sp-filter': 'Concentrate thickening & filtration',
  'sp-paste': 'Tails thickening & paste backfill',
  'sp-water': 'Process water system',
};

export function nodeHumanName(nodeId: string, subprocess?: Subprocess): string {
  return NODE_HUMAN_NAME[nodeId] ?? subprocess?.name ?? nodeId;
}

export type InsightCell = {
  text: string;
  tags: readonly string[];
};

export type InsightTableRow = {
  good: InsightCell;
  bad: InsightCell;
  insight: InsightCell;
};

const emptyCell = (): InsightCell => ({ text: '', tags: [] });

/** Zip good / bad / insight into C2's three-column table rows. */
export function insightTable(groups: readonly InsightGroup[]): InsightTableRow[] {
  const rowsByKind = new Map(groups.map((group) => [group.kind, group.rows]));
  const maxLen = Math.max(
    rowsByKind.get('good')?.length ?? 0,
    rowsByKind.get('bad')?.length ?? 0,
    rowsByKind.get('insight')?.length ?? 0,
    0,
  );
  if (maxLen === 0) return [];

  return Array.from({ length: maxLen }, (_, index) => {
    const cell = (kind: InsightKind): InsightCell => {
      const row = rowsByKind.get(kind)?.[index];
      return row ? { text: row.text, tags: row.tags } : emptyCell();
    };
    return {
      good: cell('good'),
      bad: cell('bad'),
      insight: cell('insight'),
    };
  });
}

export const RULE_ROLE_LABEL: Record<DiagnosticRule['responsible'], string> = {
  operator: 'Operator',
  metallurgist: 'Control metallurgist',
  maintenance: 'Maintenance',
  superintendent: 'Superintendent',
};

export const RULE_PRIORITY_TONE: Record<
  DiagnosticRule['priority'],
  'danger' | 'warning' | 'default'
> = {
  P1: 'danger',
  P2: 'warning',
  P3: 'default',
  P4: 'default',
};

/** Derive: crusher→paste strip from STREAMS. Unranked nodes stay subordinate. */
export function plantStrip(
  ranks: readonly ResolvedUtilizationRank[],
  streams: readonly Stream[] = STREAMS,
  loops: readonly RecirculatingLoop[] = RECIRCULATING_LOOPS,
  subprocesses: readonly Subprocess[] = SUBPROCESSES,
  units: readonly UnitOperation[] = UNIT_OPERATIONS,
  readings: readonly Reading[] = READINGS,
): PlantStripNode[] {
  return stripNodeOrder(streams, subprocesses).map((id) => {
    const rank = rankForNode(id, ranks);
    const step = subprocesses.find((row) => row.id === id);
    const utility = id === 'sp-water';
    const recycleBadge = utility
      ? waterRecycleBadge(streams)
      : recycleBadgeForNode(id, streams, loops);
    const primary = primaryUnitForNode(id, units);
    let headline: string | null;
    let sub: string | null;
    if (utility || !primary) {
      headline = '—';
      sub = recycleBadge;
    } else {
      const parts = splitKeyLabel(formatUnitKeyParam(primary.id, primary.keyReadingIds, readings));
      headline = parts.headline;
      sub = parts.sub;
      // D14: the plant strip reads fresh feed on the same daily mass basis.
      if (id === 'sp-grind' || id === 'sp-crush') {
        const ore = readings.find((row) => row.id === 'rd-kpi-ore-td')!;
        headline = `${(ore.value! / 24).toFixed(1)} stph`;
        const detail = id === 'sp-grind'
          ? `P80 ${readings.find((row) => row.id === 'rd-spec-p80')!.design} µm design`
          : 'CSS 110 mm';
        sub = `${(ore.design! / 24).toFixed(1)} design\n${detail}`;
      }
    }
    return {
      id,
      shortName: STRIP_SHORT_NAME[id] ?? step?.name ?? id,
      headline,
      sub,
      utilizationPct: rank?.utilizationPct ?? null,
      isBottleneck: rank?.isBottleneck ?? false,
      recycleBadge,
      utility: utility || undefined,
    };
  });
}

export type SampleChip = SamplePoint & {
  hostStreamName: string | null;
  hostNodeId: string;
};

/** Attach each sample to its STREAMS edge; SA02/SA06 stay on sp-pb. */
export function sampleChips(
  points: readonly SamplePoint[],
  streams: readonly Stream[] = STREAMS,
): SampleChip[] {
  return points.map((point) => {
    const edge = streams.find((row) => row.samplePointIds.includes(point.id));
    const hostNodeId = edge
      ? edge.from.startsWith('sp-')
        ? edge.from
        : edge.to
      : INTERIOR_SAMPLE_NODE;
    return {
      ...point,
      hostStreamName: edge?.name ?? null,
      hostNodeId,
    };
  });
}

export type HardnessStat = {
  label: string;
  value: string;
  sub: string;
};

export type HardnessMeter = {
  value: number;
  max: number;
  design: number;
  band: { low: number; high: number };
  tone: VizTone;
};

export type HardnessTrend = {
  categories: readonly string[];
  points: readonly number[];
  synthetic: boolean;
};

function jt023TrendPoints(state: SessionState): { categories: string[]; points: number[] } {
  const view = seriesAt('rs-jt-023', state);
  const { start } = shiftOf(state.cursor);
  const hourly = view.points.filter((p) => p.value !== null && diffMinutes(p.at, start) >= 0);
  const categories = hourly.map((p) => {
    const hm = p.at.split('T')[1] ?? '';
    return hm.slice(0, 5);
  });
  const points = hourly.map((p) => p.value as number);
  if (points.length === 0) {
    const idle = view.latest?.value ?? 12.8;
    return { categories: ['now'], points: [idle] };
  }
  categories.push('now');
  points.push(view.latest?.value ?? points[points.length - 1]!);
  return { categories, points };
}

function formatKwValue(value: number, decimals: number, rising: boolean): string {
  const base = value.toFixed(decimals);
  return rising ? `${base} ↑` : base;
}

/** Daily measured fresh feed alongside the hourly JT-023 hardness flag. */
export function hardnessPair(
  phase: ScenarioPhase,
  state: SessionState,
): {
  feed: HardnessStat & { meter: HardnessMeter; chart: ControlChartViewModel };
  kw: HardnessStat & { trend: HardnessTrend; chart: ControlChartViewModel };
} {
  const sessionReadings = readingsAt(state);
  const jt = sessionReadings.find((row) => row.id === 'rd-jt023-kwpt');
  const feed = sessionReadings.find((row) => row.id === 'rd-unit-bm01-feed')!;
  const ore = sessionReadings.find((row) => row.id === 'rd-kpi-ore-td')!;
  const kwVal = jt?.value ?? 12.8;
  const decimals = jt?.decimals ?? 1;
  const feedVal = feed.value!;
  const feedStr = formatValue(feed, { withUnit: true });
  const trendData = jt023TrendPoints(state);

  const feedMeter: HardnessMeter = {
    value: feedVal,
    max: 120,
    design: feed.design!,
    band: { low: feed.band!.lo!, high: feed.band!.hi! },
    tone: 'ok',
  };
  const feedChart = { ...controlChart('mv-ore-td', state), readingLabel: `${ore.label} (${ore.unit})` };
  const kwChart = controlChart('mv-jt-023', state);

  if (phase === 'provisional') {
    return {
      feed: {
        label: 'WIT-021 fresh feed · daily average',
        value: feedStr,
        sub: `vs ${feed.design!.toFixed(1)} stph design`,
        meter: feedMeter,
        chart: feedChart,
      },
      kw: {
        label: 'JT-023 kW / tph',
        value: formatKwValue(kwVal, decimals, true),
        sub: 'Rising · feed steady · hardness, not media wear',
        trend: {
          categories: trendData.categories,
          points: trendData.points,
          synthetic: true,
        },
        chart: kwChart,
      },
    };
  }
  if (phase === 'confirmed') {
    return {
      feed: {
        label: 'WIT-021 fresh feed · daily average',
        value: feedStr,
        sub: `vs ${feed.design!.toFixed(1)} stph design · cut 10% recommended`,
        meter: feedMeter,
        chart: feedChart,
      },
      kw: {
        label: 'JT-023 kW / tph',
        value: formatKwValue(kwVal, decimals, true),
        sub: 'Still rising · SA01 confirmed coarsening',
        trend: {
          categories: trendData.categories,
          points: trendData.points,
          synthetic: true,
        },
        chart: kwChart,
      },
    };
  }
  return {
    feed: {
      label: 'WIT-021 fresh feed · daily average',
      value: feedStr,
      sub: `vs ${feed.design!.toFixed(1)} stph design`,
      meter: feedMeter,
      chart: feedChart,
    },
    kw: {
      label: 'JT-023 kW / tph',
      value: formatKwValue(kwVal, decimals, false),
      sub: 'In band · not the hardness flag',
      trend: {
        categories: trendData.categories,
        points: trendData.points,
        synthetic: true,
      },
      chart: kwChart,
    },
  };
}
function readingToneToViz(tone: ReadingTone): VizTone {
  if (tone === 'bad') return 'bad';
  if (tone === 'warn') return 'warn';
  if (tone === 'ok') return 'ok';
  return 'neutral';
}

function windowFromReading(reading: Reading): ConstraintWindow | null {
  const override = CONSTRAINT_DISPLAY_OVERRIDE[reading.id];
  const dcs = reading.dcsRange;
  if (dcs?.hi === undefined && dcs?.lo === undefined) return null;

  const dcsLo = dcs?.lo ?? 0;
  const dcsHi = dcs?.hi ?? dcsLo;
  const band = reading.band
    ? { low: reading.band.lo ?? dcsLo, high: reading.band.hi ?? dcsHi }
    : null;
  const design = override ? null : reading.design ?? null;
  const value =
    reading.value !== undefined
      ? reading.value
      : design !== null
        ? design
        : null;
  const alarm = reading.alarms?.H ?? reading.alarms?.HH ?? null;
  const tone = readingToneToViz(readingStatus(reading).tone);

  if (value === null) {
    return {
      max: dcsHi,
      band,
      value: null,
      design: null,
      alarm,
      tone: 'neutral',
    };
  }

  return {
    max: dcsHi,
    band,
    value,
    design,
    alarm,
    tone,
  };
}

export type ConstraintWindow = {
  max: number;
  band: { low: number; high: number } | null;
  value: number | null;
  design: number | null;
  alarm: number | null;
  tone: VizTone;
};

export type ConstraintRowView = ProcessConstraint & {
  window: ConstraintWindow | null;
  chart?: ControlChartViewModel;
  seriesId?: string;
};

function monitoredVariableForConstraint(row: ProcessConstraint): string | undefined {
  const readingId = `rd-${row.id}`;
  return MONITORED_VARIABLES.find((variable) => variable.readingId === readingId)?.id;
}

/** Derive operating-window meter per C1 §8.2 row from Reading tuples. */
export function constraintRows(
  constraints: readonly ProcessConstraint[],
  readings: readonly Reading[] = READINGS,
  state?: SessionState,
): ConstraintRowView[] {
  return constraints.map((row) => {
    const reading = constraintReadingFor(row, readings);
    if (!reading) return { ...row, window: null };
    const variableId = monitoredVariableForConstraint(row);
    const chart = state && variableId ? controlChart(variableId, state) : undefined;
    const series = MONITORED_VARIABLES.find((variable) => variable.id === variableId);
    return {
      ...row,
      window: windowFromReading(reading),
      chart,
      seriesId: series?.seriesId,
    };
  });
}

export type NodeRagLabel = 'GREEN' | 'AMBER' | 'RED';

export type NodeSummary = {
  shortName: string;
  ragLabel: string;
  ragState: 'online' | 'warning' | 'blocked';
  why: string;
  windowHeadline: string;
  displayCount: number;
  looksLikeCount: number;
};

const TONE_SEVERITY: Partial<Record<VizTone, number>> = {
  bad: 3,
  warn: 2,
  ok: 1,
  neutral: 0,
  info: 0,
};

function toneToRag(tone: VizTone): {
  ragLabel: NodeRagLabel;
  ragState: 'online' | 'warning' | 'blocked';
} {
  if (tone === 'bad') return { ragLabel: 'RED', ragState: 'blocked' };
  if (tone === 'warn') return { ragLabel: 'AMBER', ragState: 'warning' };
  return { ragLabel: 'GREEN', ragState: 'online' };
}

function rankForStripNode(
  nodeId: string,
  ranks: readonly UtilizationRank[],
): UtilizationRank | undefined {
  return ranks.find((row) => row.id === `u-${nodeId.slice(3)}`);
}

function worstConstraintRow(
  rows: readonly ConstraintRowView[],
): ConstraintRowView | null {
  let worst: ConstraintRowView | null = null;
  let worstScore = -1;
  for (const row of rows) {
    const tone = row.window?.tone ?? 'neutral';
    const score = TONE_SEVERITY[tone] ?? 0;
    if (score > worstScore) {
      worstScore = score;
      worst = row;
    }
  }
  return worst;
}

function constraintRowWhy(row: ConstraintRowView): string {
  const tone = row.window?.tone ?? 'neutral';
  if (tone === 'bad') return `${row.point} is outside the normal band`;
  if (tone === 'warn') return `${row.point} is in the DCS band but outside normal`;
  return `${row.point} is in the normal band`;
}

function bottleneckUtilWhy(
  nodeId: string,
  ranks: readonly ResolvedUtilizationRank[],
): string {
  const rank = rankForStripNode(nodeId, ranks);
  const margin = headroomToRunnerUp(ranks);
  const pct = rank?.utilizationPct ?? 0;
  return `${pct}% utilization · ${margin} pt margin over runner-up`;
}

/** Section summary labels, e.g. `Displays (3)`. */
export function sectionCountLabel(base: string, count: number): string {
  return `${base} (${count})`;
}

/** Pure: RAG headline + counts for node-detail summary mode. */
export function nodeSummary(
  nodeId: string,
  input: {
    phase: ScenarioPhase;
    ranks: readonly ResolvedUtilizationRank[];
    constraintRows: readonly ConstraintRowView[];
    insightGroups: readonly InsightGroup[];
    state: SessionState;
    advisory?: AdvisorySessionState;
  },
): NodeSummary {
  const shortName = STRIP_SHORT_NAME[nodeId] ?? nodeId;
  const displayCount = nodeDisplays(nodeId).length;
  const looksLikeCount = input.insightGroups.reduce(
    (sum, group) => sum + group.rows.length,
    0,
  );
  const isBottleneck = rankForStripNode(nodeId, input.ranks)?.isBottleneck ?? false;

  let ragLabel: string;
  let ragState: 'online' | 'warning' | 'blocked';
  let why: string;

  if (isBottleneck) {
    const colorLabel = input.phase === 'idle' ? 'AMBER' : 'RED';
    ragLabel = `${colorLabel} · bottleneck read`;
    ragState = input.phase === 'idle' ? 'warning' : 'blocked';
    why =
      nodeId === 'sp-grind'
        ? bottleneckRead(input.state, input.advisory, { omitFeed: true })
        : bottleneckUtilWhy(nodeId, input.ranks);
  } else if (input.constraintRows.length === 0) {
    ({ ragLabel, ragState } = { ragLabel: 'GREEN', ragState: 'online' });
    why = 'No constraint rows seeded for this stage.';
  } else {
    const worst = worstConstraintRow(input.constraintRows)!;
    ({ ragLabel, ragState } = toneToRag(worst.window?.tone ?? 'neutral'));
    why = constraintRowWhy(worst);
  }

  let windowHeadline: string;
  if (input.constraintRows.length === 0) {
    windowHeadline = 'No operating window seeded';
  } else {
    const worst = worstConstraintRow(input.constraintRows);
    const worstLabel = toneToRag(worst?.window?.tone ?? 'neutral').ragLabel;
    const bandWord = input.constraintRows.length === 1 ? 'window band' : 'window bands';
    windowHeadline = `worst ${worstLabel.toLowerCase()} · ${input.constraintRows.length} ${bandWord}`;
  }

  return {
    shortName,
    ragLabel,
    ragState,
    why,
    windowHeadline,
    displayCount,
    looksLikeCount,
  };
}

export type NodeStreamRow = {
  stream: Stream;
  direction: 'in' | 'out' | 'interior';
  specs: StreamSpec[];
  loop: RecirculatingLoop | null;
};

export type InsightGroup = {
  kind: InsightKind;
  rows: NodeInsight[];
};

export type DiagnosticRuleRow = DiagnosticRule & {
  highlighted: boolean;
  contrast: boolean;
};

export type NodeDetail = {
  subprocess: Subprocess | undefined;
  constraints: ProcessConstraint[];
  units: UnitOperation[];
  ranks: ResolvedUtilizationRank[];
  insightGroups: InsightGroup[];
  streams: NodeStreamRow[];
  rules: DiagnosticRuleRow[];
  rulesLine: string | null;
};

function fmeaRevLabel(): string {
  const match = FMEA_VINTAGE.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return FMEA_VINTAGE;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${match[3]} ${months[Number(match[2]) - 1]}`;
}

/** The ingested workbook rules attached to a stage through the tag map
 *  (construct.R7) — the one row set the Rules tab renders and every rule
 *  count on the plant model reads (polish.P11 adjudication 3). */
export function attachedNodeRules(nodeId: string): DiagnosticRule[] {
  const ruleIds = [
    ...new Set(
      RULE_CLAIMS.filter((claim) => claim.subject.kind === 'rule' && isIngestedRuleClaim(claim)).map(
        (claim) => claim.subject.id,
      ),
    ),
  ];
  return rulesForNode(
    nodeId,
    ruleIds.map((id) => ruleById(id)).filter((row): row is DiagnosticRule => row != null),
  );
}

/** `5 rules · FMEA rev 06 Jun` for a count of rendered rule rows; null at zero. */
export function ruleCountLine(count: number): string | null {
  if (count === 0) return null;
  return `${count} rule${count === 1 ? '' : 's'} · FMEA rev ${fmeaRevLabel()}`;
}

/** One line per attached rule count for node detail (construct.R7). */
export function nodeRuleProvenanceLine(nodeId: string): string | null {
  return ruleCountLine(attachedNodeRules(nodeId).length);
}

/** Filter: Appendix B by subprocessId + insights + in/out + grind rules. */
export function nodeDetail(
  nodeId: string,
  input: {
    subprocesses: readonly Subprocess[];
    constraints: readonly ProcessConstraint[];
    units: readonly UnitOperation[];
    ranks: readonly ResolvedUtilizationRank[];
    streams?: readonly Stream[];
    specs?: readonly StreamSpec[];
    loops?: readonly RecirculatingLoop[];
    insights?: readonly NodeInsight[];
    rules?: readonly DiagnosticRule[];
  },
  phase: ScenarioPhase = 'idle',
): NodeDetail {
  const areas = new Set(NODE_AREAS[nodeId] ?? []);
  const extraRankIds =
    nodeId === 'sp-grind' ? new Set(['u-cyc', 'u-mill-kw']) : new Set<string>();
  const streams = input.streams ?? STREAMS;
  const specs = input.specs ?? STREAM_SPECS;
  const loops = input.loops ?? RECIRCULATING_LOOPS;
  const insights = input.insights ?? NODE_INSIGHTS;
  // polish.P11 (adjudication 3): the Rules tab renders the rules attached to
  // this stage — the same rows its count reads — not the Module 1 seed list,
  // which only ever carried grinding's four.
  const rules = input.rules ? rulesForNode(nodeId, input.rules) : attachedNodeRules(nodeId);
  const playing = phase !== 'idle';

  const nodeInsights = insights.filter((row) => row.subprocessId === nodeId);
  const insightGroups: InsightGroup[] = INSIGHT_KIND_ORDER.flatMap((kind) => {
    const rows = nodeInsights.filter((row) => row.kind === kind);
    return rows.length > 0 ? [{ kind, rows }] : [];
  });

  const exteriorRows: NodeStreamRow[] = streams
    .filter(
      (edge) =>
        !isInteriorStream(edge.from, edge.to) &&
        (edge.to === nodeId || edge.from === nodeId),
    )
    .map((edge) => ({
      stream: edge,
      direction: edge.to === nodeId ? ('in' as const) : ('out' as const),
      specs: specs.filter((spec) => spec.streamId === edge.id),
      loop: loops.find((row) => row.streamId === edge.id) ?? null,
    }));

  const interiorRows: NodeStreamRow[] = streams
    .filter(
      (edge) =>
        isInteriorStream(edge.from, edge.to) && subprocessForNodeId(edge.from) === nodeId,
    )
    .map((edge) => ({
      stream: edge,
      direction: 'interior' as const,
      specs: specs.filter((spec) => spec.streamId === edge.id),
      loop: loops.find((row) => row.streamId === edge.id) ?? null,
    }));

  const streamRows: NodeStreamRow[] = [...exteriorRows, ...interiorRows];

  return {
    subprocess: input.subprocesses.find((step) => step.id === nodeId),
    constraints: input.constraints.filter((row) => row.subprocessId === nodeId),
    units: input.units.filter((unit) => areas.has(unit.area)),
    ranks: input.ranks.filter((row) => extraRankIds.has(row.id)),
    insightGroups,
    streams: streamRows,
    rules: rules.map((rule) => ({
      ...rule,
      highlighted: playing && nodeId === 'sp-grind' && rule.id === 'dr-hardness',
      contrast: playing && nodeId === 'sp-grind' && rule.id === 'dr-media',
    })),
    rulesLine: ruleCountLine(rules.length),
  };
}

export function defaultStripNodeId(): string {
  return 'sp-grind';
}

export type StripNavItem = {
  id: string;
  label: string;
};

/** Map strip nodes to subprocess tab items (shortName labels). */
export function stripNavItems(strip: readonly PlantStripNode[]): StripNavItem[] {
  return strip.map((node) => ({ id: node.id, label: node.shortName }));
}

/** Key-param strip label — readings projected; BM01 scenario keeps pending SA01 prose. */
export function unitKeyParamLabel(
  unit: UnitOperation,
  phase: ScenarioPhase,
  readings: readonly Reading[],
): string {
  if (unit.id === 'BM01' && phase === 'provisional') {
    const feed = readings.find((row) => row.id === 'rd-unit-bm01-feed');
    const feedLabel = feed
      ? formatUnitKeyParam('BM01', [feed.id], readings).split(' · ')[0]
      : formatUnitKeyParam('BM01', ['rd-unit-bm01-feed'], READINGS).split(' · ')[0];
    return `${feedLabel} · P80 pending SA01`;
  }
  return formatUnitKeyParam(unit.id, unit.keyReadingIds, readings);
}

export function unitHealth(
  unit: UnitOperation,
  phase: ScenarioPhase,
  readings: readonly Reading[],
  state?: SessionState,
  subprocessId?: string,
): UnitHealth {
  if (unit.id === 'BM01' && phase !== 'idle') {
    return 'alarm';
  }
  if (state && subprocessId) {
    return readingToneToHealth(
      scopeHealth({ kind: 'subprocess', id: subprocessId }, readings, state),
    );
  }
  return readingToneToHealth(healthForReadingIds(unit.keyReadingIds, readings, state));
}

/** Units are static; health and key params derive from readings at render time. */
export function applyUnits(units: readonly UnitOperation[]): UnitOperation[] {
  return units.map((unit) => ({ ...unit }));
}

export { scopeHealth } from '@/versions/v1/shared/reading.derive';
export { grindOperatingCurve } from '@/versions/v1/shared/operating-curve.derive';
